import { Box, Clone, useAnimations } from "@react-three/drei";
import { useEffect, useState, useRef } from "react";
import * as THREE from "three";
import GlbLoader from "../Utils/GlbLoader";
import { stateInAreaRooms, stateWallPush, statePlayerPositionS } from "../store";
import { useSnapshot } from "valtio";

import stateMuseumPoints from "./states/stateMuseumPoints";

export default function Pointer({ setDoorCloseAnimationEnd }) {
  const [clones, setClones] = useState([]);

  const snapWallPush = useSnapshot(stateWallPush);

  const handlePointerDown = (index) => {
    if (index === snapWallPush.activeThemeIndex) {
      stateWallPush.activeThemeIndex = -1;
    } else if (
      stateWallPush.activeThemeIndex != -1 &&
      index != snapWallPush.activeThemeIndex
    ) {
      stateWallPush.activeThemeIndex = -1;
    } else {
      stateWallPush.activeThemeIndex = index;
    }
  };

  const clonesData = useRef(
    stateMuseumPoints.map((point, x) => ({
      key: x,
      position: [...point.Position],
      rotation: [...point.Rotation],
      index: [...point.Id],
      Id: x,
    }))
  ).current;

  useEffect(() => {
    setClones(clonesData);
  }, [clonesData]);

  return (
    <>
      {clones.map((clone) => (
        <PointerKon
          key={clone.key}
          position={clone.position}
          rotation={clone.rotation}
          Id={clone.Id}
          onPointerDown={() => handlePointerDown(clone.Id)}
          // 🔧 pass the reactive snapshot value, not the raw state — this is what
          // actually drives door open/close now, instead of relying on onPointerDown's
          // identity changing as a side-channel "something changed" signal.
          active={snapWallPush.activeThemeIndex}
          setDoorCloseAnimationEnd={setDoorCloseAnimationEnd}
        />
      ))}
    </>
  );
}

function PointerKon({
  scale,
  position,
  rotation,
  onPointerDown,
  Id,
  setDoorCloseAnimationEnd,
  active,
}) {
  const rotationInRadians = [
    THREE.MathUtils.degToRad(rotation[0]),
    THREE.MathUtils.degToRad(-rotation[2]),
    THREE.MathUtils.degToRad(rotation[0]),
  ];

  const { pointerModel, doorButton, buttonHoverMaterial, buttonOriginalMaterial } = GlbLoader();

  const ref = useRef();
  const { actions, names } = useAnimations(pointerModel.animations, ref);

  const snapStateInAreaRooms = useSnapshot(stateInAreaRooms);
  const snapPlayerPosition = useSnapshot(statePlayerPositionS);

  const [hovered, setHover] = useState();
  const ButtonRef = useRef();
  const [doorClosed, setDoorClosed] = useState();
  const [firstPush, setFirstPush] = useState(false);
  const highlightRef = useRef();

  useEffect(() => {
    if (!ButtonRef.current) return;
    highlightRef.current = ButtonRef.current.getObjectByName("Button001");
    if (highlightRef.current) {
      highlightRef.current.material = doorButton.material;
    }
  }, [doorButton]);

  const ButtonToHover = () => {
    if (highlightRef.current) {
      highlightRef.current.material = buttonHoverMaterial;
    }
  };

  const ButtonToOriginal = () => {
    if (highlightRef.current) {
      highlightRef.current.material = buttonOriginalMaterial;
    }
  };

  // 🔧 Single source of truth for "should this door be open right now":
  // this door is the active one AND we're currently standing in EntryRoom.
  // Replaces the two separate, overlapping effects that used to exist
  // (one keyed off onPointerDown's identity, one off inAreaRoomEntryRoom) —
  // both were really reacting to the same underlying question.
  const shouldBeOpen = active === Id && snapStateInAreaRooms.inAreaRoomEntryRoom;

  useEffect(() => {
    const action = actions?.[names?.[0]];
    if (!action || !ref.current) return;

    action.clampWhenFinished = true;
    action.setLoop(THREE.LoopOnce, 1);

    let mixer = null;
    let onFinished = null;

    if (shouldBeOpen) {
      stateWallPush.visibleThemeIndex = Id;
    }

    if (shouldBeOpen && doorClosed) {
      ButtonToHover();
      action.reset();
      action.timeScale = 1;
      action.play();
      setDoorClosed(false);
    } else if (!shouldBeOpen && !doorClosed) {
      ButtonToOriginal();
      action.paused = false;
      action.reset();
      action.time = action.getClip().duration;
      action.timeScale = -1;
      action.play();
      setDoorClosed(true);

      mixer = action.getMixer();
      onFinished = (e) => {
        if (e.action !== action) return;
        mixer.removeEventListener('finished', onFinished);
        // Only clear when player is NOT inside the room (door closed behind = keep visible)
        if (stateWallPush.visibleThemeIndex === Id && !stateInAreaRooms.inAreaRoom) {
          stateWallPush.visibleThemeIndex = -1;
        }
      };
      mixer.addEventListener('finished', onFinished);
    }

    if (!firstPush) {
      setFirstPush(true);
    }

    return () => {
      action.stop();
      if (mixer && onFinished) mixer.removeEventListener('finished', onFinished);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldBeOpen]);

  const handlePointerDown = () => {
    if (onPointerDown) {
      onPointerDown();
    }
  };

  const handlePointerOver = () => {
    setHover(true);
    if (doorClosed) {
      ButtonToHover();
    }
  };

  const handlePointerOut = () => {
    setHover(false);
    if (doorClosed) {
      ButtonToOriginal();
    }
  };

  useEffect(() => {
    if (!snapPlayerPosition.isTracking) {
      document.body.style.cursor = hovered ? "pointer" : "auto";
    }
  }, [hovered, snapPlayerPosition.isTracking]);

  return (
    <>
      <Clone scale={scale} position={position} rotation={rotationInRadians} object={pointerModel.scene} ref={ref}>
        <Box
          args={[1, 1, 1]}
          position={[-0.8, 24, -40]}
          scale={[3, 20, 20]}
          visible={false}
          onClick={handlePointerDown}
          onPointerOver={handlePointerOver}
          onPointerOut={handlePointerOut}
        ></Box>
        <Clone
          ref={ButtonRef}
          object={doorButton.scene}
          position={[-0.8, 13, -40]}
          scale={1.4}
          visible={snapStateInAreaRooms.inAreaRoomEntryRoom}
        ></Clone>
      </Clone>
    </>
  );
}
